import React, { useState, useEffect, useRef, memo, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCredit } from '../contexts/CreditContext';
import logger from '../utils/logger';
import { useParams } from 'react-router-dom';
import { Message, ChatSession, createChatSession, getChatSessions, sendMessage, streamChat, deleteChatSession, getChatSession, uploadChatAttachment, renameChatSession, toggleFavoriteSession, getFavoriteSessions } from '../services/chatService';
import { useChatSessions, useChatSession, useCreateChatSession, useDeleteChatSession } from '../hooks/useChat';
import ChatSkeleton from './skeletons/ChatSkeleton';
import { useQueryClient } from '@tanstack/react-query';
import { Button, TextField, Box, Typography, Paper, CircularProgress, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme, Drawer, Alert, Chip, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import SendIcon from '@mui/icons-material/Send';
import MenuIcon from '@mui/icons-material/Menu';
import ChatIcon from '@mui/icons-material/Chat';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Menu, MenuItem, InputAdornment } from '@mui/material';
import { Send } from '@mui/icons-material';
import AudioRecorder from './AudioRecorder';
import { useNavigate } from 'react-router-dom';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ChatMessageFeedback, FeedbackWidget } from './feedback';
import FlowSuggestionCard from './FlowSuggestionCard';


// =====================================================================
// MessageAttachments — renderiza os arquivos anexados a uma mensagem
// (persistidos no histórico + otimista imediatamente após envio).
// Baixa a mídia via fetch autenticado para poder exibir preview de imagens
// (o endpoint /api/v1/chat/attachments/{id}/preview exige Bearer token).
// =====================================================================
interface MessageAttachmentItem {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string;
  preview?: string | null;
}

const getApiBase = () => (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');

const authFetchBlobUrl = async (relativeUrl: string): Promise<string | null> => {
  try {
    if (!relativeUrl) return null;
    const token = localStorage.getItem('token');
    // Normalize URL: backend may return "/api/v1/..." – strip prefix and re-attach base
    let url = relativeUrl;
    if (url.startsWith('/api/v1/')) {
      url = getApiBase() + url.slice('/api/v1'.length);
    } else if (url.startsWith('/')) {
      url = getApiBase().replace(/\/api\/v1$/, '') + url;
    }
    const resp = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return URL.createObjectURL(blob);
  } catch (e) {
    return null;
  }
};

const AttachmentImage = memo(function AttachmentImage({ url, alt, onClick }: { url: string; alt: string; onClick?: () => void }) {
  const [src, setSrc] = React.useState<string | null>(null);
  React.useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    authFetchBlobUrl(url).then((blobUrl) => {
      if (cancelled) {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        return;
      }
      objectUrl = blobUrl;
      setSrc(blobUrl);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  if (!src) {
    return (
      <Box sx={{ width: 180, height: 120, borderRadius: 1.5, bgcolor: 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onClick={onClick}
      sx={{
        maxWidth: 240,
        maxHeight: 200,
        borderRadius: 1.5,
        cursor: onClick ? 'pointer' : 'default',
        objectFit: 'cover',
        display: 'block',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        '&:hover': onClick ? { opacity: 0.9 } : undefined
      }}
    />
  );
});

const MessageAttachments: React.FC<{ attachments?: MessageAttachmentItem[]; isUser: boolean }> = ({ attachments, isUser }) => {
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewSrc, setPreviewSrc] = React.useState<string | null>(null);
  const [previewName, setPreviewName] = React.useState<string>('');
  const theme = useTheme();

  if (!attachments || attachments.length === 0) return null;

  const handleDownload = async (att: MessageAttachmentItem) => {
    if (!att.url) return;
    const blobUrl = await authFetchBlobUrl(att.url);
    if (!blobUrl) return;
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = att.name || 'arquivo';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
  };

  const handlePreviewImage = async (att: MessageAttachmentItem) => {
    const src = att.url ? await authFetchBlobUrl(att.url) : null;
    if (!src) return;
    setPreviewSrc(src);
    setPreviewName(att.name || '');
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewSrc) {
      URL.revokeObjectURL(previewSrc);
      setPreviewSrc(null);
    }
  };

  return (
    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {attachments.map((att) => {
        const isImage = att.type === 'image' || /\.(png|jpg|jpeg|gif|webp|bmp)$/i.test(att.name);
        const isAudio = att.type === 'audio' || /\.(mp3|wav|m4a|ogg|webm)$/i.test(att.name);
        const previewUrl = att.preview || att.url;
        return (
          <Box
            key={att.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              pr: 1.2,
              borderRadius: 1.5,
              bgcolor: isUser ? 'rgba(255,255,255,0.15)' : alpha(theme.palette.primary.main, 0.06),
              border: `1px solid ${isUser ? 'rgba(255,255,255,0.25)' : alpha(theme.palette.primary.main, 0.15)}`,
              maxWidth: '100%'
            }}
          >
            {isImage && previewUrl ? (
              <AttachmentImage url={previewUrl} alt={att.name} onClick={() => handlePreviewImage(att)} />
            ) : (
              <>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: isUser ? 'rgba(255,255,255,0.2)' : alpha(theme.palette.primary.main, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  {isAudio ? (
                    <AttachFileIcon fontSize="small" sx={{ color: isUser ? 'white' : 'primary.main' }} />
                  ) : (
                    <DescriptionIcon fontSize="small" sx={{ color: isUser ? 'white' : 'primary.main' }} />
                  )}
                </Box>
                <Box sx={{ minWidth: 0, maxWidth: 220 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: isUser ? 'white' : 'text.primary', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {att.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isUser ? 'rgba(255,255,255,0.75)' : 'text.secondary', display: 'block' }}>
                    {att.size ? `${(att.size / 1024).toFixed(1)} KB` : ''}
                  </Typography>
                </Box>
                <Tooltip title="Baixar">
                  <IconButton size="small" onClick={() => handleDownload(att)} sx={{ color: isUser ? 'white' : 'primary.main' }}>
                    <AttachFileIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        );
      })}

      <Dialog open={previewOpen} onClose={closePreview} maxWidth="lg" fullWidth
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none' } }}>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: 'rgba(0,0,0,0.9)', position: 'relative' }}>
          <IconButton onClick={closePreview} sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.4)' }}>
            <CloseIcon />
          </IconButton>
          {previewSrc && (
            <Box component="img" src={previewSrc} alt={previewName} sx={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Styled components with responsive heights for proper scrolling
const ChatContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 64px)', // Usar toda a altura disponível menos header
  width: '100%',
  overflow: 'hidden',
  position: 'relative',
  maxWidth: 'none',
  margin: 0,
  padding: 0,
  [theme.breakpoints.down('md')]: {
    height: 'calc(100vh - 64px)',
  },
  [theme.breakpoints.down('sm')]: {
    height: 'calc(100vh - 64px)',
  },
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  height: 'calc(100vh - 134px)', // Ajustar para nova altura do container
  width: '100%',
  maxWidth: 'none',
  margin: 0,
  [theme.breakpoints.down('md')]: {
    height: 'calc(100vh - 134px)',
    padding: '16px',
  },
  [theme.breakpoints.down('sm')]: {
    height: 'calc(100vh - 134px)',
    padding: '8px',
  },
}));

interface MessageBubbleProps {
  theme?: any;
  isUser: boolean;
}

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser'
})<MessageBubbleProps>(({ theme, isUser }) => ({
  padding: '12px 16px',
  borderRadius: '8px',
  maxWidth: '90%',
  marginBottom: '8px',
  wordBreak: 'break-word',
  ...(isUser && {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    alignSelf: 'flex-end',
    borderBottomRightRadius: 0,
  }),
  ...(!isUser && {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
    alignSelf: 'flex-start',
    borderBottomLeftRadius: 0,
  }),
}));

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px 20px',
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  bottom: 0,
  zIndex: 1100,
  minHeight: '70px',
  maxHeight: '45vh',
  overflowY: 'auto',
  width: '100%',
  maxWidth: 'none',
  margin: 0,
  '& .MuiTextField-root': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  }
}));

// Sidebar responsiva
const Sidebar = styled(Paper)(({ theme }) => ({
  width: '340px',
  minWidth: '340px',
  flexShrink: 0,
  padding: '16px',
  height: 'calc(100vh - 64px)', // Usar toda a altura disponível
  overflowY: 'auto',
  backgroundColor: '#ffffff',
  transition: 'all 0.3s ease',
  borderRadius: 0,
  boxShadow: 'none',
  margin: 0,
  [theme.breakpoints.down('md')]: {
    width: '300px',
    minWidth: '300px',
    height: 'calc(100vh - 64px)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    minWidth: '100%',
    height: 'calc(100vh - 64px)',
    borderRadius: 0,
  },
}));

// Layout principal responsivo
const ChatLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  height: 'calc(100vh - 64px)', // Usar toda a altura disponível
  position: 'relative',
  width: '100%',
  maxWidth: 'none',
  margin: 0,
  padding: 0,
  [theme.breakpoints.down('md')]: {
    height: 'calc(100vh - 64px)',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    height: 'calc(100vh - 64px)',
  },
}));

// Conteúdo principal responsivo
const MainContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sidebarOpen'
})<{ sidebarOpen: boolean }>(({ theme, sidebarOpen }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  width: '100%',
  maxWidth: 'none',
  margin: 0,
  padding: 0,
}));

// Botão toggle da sidebar
const SidebarToggle = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '16px',
  left: '16px',
  zIndex: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  display: 'none',
  margin: 0,
  [theme.breakpoints.down('md')]: {
    display: 'flex',
  },
}));



// Mensagem de boas-vindas
const WelcomeMessage = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.text.primary,
  width: '100%',
  maxWidth: 'none',
  boxShadow: 'none',
  margin: '0 0 16px 0',
  borderRadius: 0,
}));



interface ChatProps {
  sessionId?: string;
  initialMessage?: string;
  initialAudioFile?: File;
}

export const Chat: React.FC<ChatProps> = ({ sessionId: propSessionId, initialMessage, initialAudioFile }) => {
  const { user, refreshUserData } = useAuth();
  const { 
    credits, 
    isBlocked, 
    shouldShowWarning, 
    showLowCreditPopup, 
    setShowLowCreditPopup 
  } = useCredit();
  const { sessionId: routeSessionId } = useParams<{ sessionId: string }>();
  const activeSessionId = propSessionId || routeSessionId;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // ⚡ React Query para cache automático de sessões
  const { data: sessionsData, isLoading: sessionsLoading } = useChatSessions(0, 25);
  
  // Estado local para sessões (necessário para paginação manual)
  const [localSessions, setLocalSessions] = useState<ChatSession[]>([]);
  
  // Memoizar sessions para evitar loop infinito
  const sessions = useMemo(() => {
    return localSessions.length > 0 ? localSessions : (sessionsData || []);
  }, [localSessions, sessionsData]);
  
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  
  // Sincronizar sessions apenas na primeira carga ou quando localSessions está vazio
  useEffect(() => {
    if (sessionsData && localSessions.length === 0) {
      setLocalSessions(sessionsData);
    }
  }, [sessionsData, localSessions.length]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Provedor removido - agora configurado via variável de ambiente no backend
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<ChatSession | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  
  // Estados para responsividade
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false); // Iniciar sempre fechado
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  // Estados para paginação
  const [sessionsSkip, setSessionsSkip] = useState(0);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const [loadingMoreSessions, setLoadingMoreSessions] = useState(false);
  const SESSIONS_LIMIT = 25;
  
  // Estados para busca
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSessions, setFilteredSessions] = useState<ChatSession[]>([]);
  
  // Estados para favoritos
  const [favoriteSessions, setFavoriteSessions] = useState<ChatSession[]>([]);
  const [showFavorites, setShowFavorites] = useState(false);
  
  // Estados para renomear
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [sessionToRename, setSessionToRename] = useState<ChatSession | null>(null);
  const [newSessionName, setNewSessionName] = useState('');
  
  // Estados para menu de contexto
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSessionForMenu, setSelectedSessionForMenu] = useState<ChatSession | null>(null);
  
  // Estados para upload de arquivos
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [fileTranscriptions, setFileTranscriptions] = useState<Map<string, string>>(new Map());
  const [fileIdMap, setFileIdMap] = useState<Map<string, string>>(new Map()); // filename -> file_id
  const [transcriptionExpanded, setTranscriptionExpanded] = useState(false); // Controla expansão da transcrição
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Estados para sugestão de wizard de documentos
  const [showWizardSuggestion, setShowWizardSuggestion] = useState(false);
  const [wizardContext, setWizardContext] = useState<string>('');
  
  // Estado para tela inicial do chat (estilo dashboard)
  const [showInitialScreen, setShowInitialScreen] = useState(true);

  // ⚡ REMOVIDO: useEffect com loadSessions
  // React Query carrega automaticamente com cache

  // REMOVIDO: Popup de créditos baixos no chat
  // Agora controlado centralmente pelo CreditContext (apenas nos thresholds 50, 30, <30)

  // Filtrar sessões baseado no termo de busca
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSessions(sessions);
    } else {
      const filtered = sessions.filter(session =>
        session.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSessions(filtered);
    }
  }, [sessions, searchTerm]);

  useEffect(() => {
    // Quando a URL passa a refletir uma sessão que acabou de ser criada/selecionada aqui,
    // o estado local já está certo; recarregar do servidor apagaria a mensagem em envio.
    if (activeSessionId && currentSession?.id !== activeSessionId) {
      loadMessages();
    }
  }, [activeSessionId]);

  // Simplificar o efeito de scroll - apenas rola quando as mensagens mudam
  useEffect(() => {
    logger.debug("Mensagens mudaram, rolando para o final");
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
      }
    }, 100);
  }, [messages]);

  // Referências para mensagens iniciais do dashboard
  const initialMessageSentRef = useRef(false);
  const initialAudioSentRef = useRef(false);
  const sessionCreationInProgressRef = useRef(false);
  const pendingInitialMessageRef = useRef<string | null>(null);
  const audioTranscriptionReadyRef = useRef(false);
  
  // Ref para mensagem pendente da tela inicial (quando usuário digita e envia antes de ter sessão)
  const pendingInitialScreenMessageRef = useRef<string | null>(null);

  // Efeito para determinar se deve mostrar a tela inicial
  // Mostrar tela inicial quando não há sessão ativa e não há mensagem do dashboard
  useEffect(() => {
    // Se há mensagem inicial do dashboard, não mostrar tela inicial
    if (initialMessage || initialAudioFile) {
      setShowInitialScreen(false);
      return;
    }
    
    // Se não há sessão atual, mostrar tela inicial
    if (!currentSession && !activeSessionId) {
      setShowInitialScreen(true);
    }
  }, [currentSession, activeSessionId, initialMessage, initialAudioFile]);

  // Efeito para enviar mensagem inicial do dashboard
  useEffect(() => {
    if (initialMessage && !initialMessageSentRef.current && !sessionCreationInProgressRef.current) {
      logger.debug("Mensagem inicial detectada do dashboard:", initialMessage);
      initialMessageSentRef.current = true;
      sessionCreationInProgressRef.current = true;
      pendingInitialMessageRef.current = initialMessage;
      
      // Criar nova sessão
      const createSession = async () => {
        try {
          const session = await handleCreateSession();
          if (session) {
            logger.debug("Sessão criada com ID:", session.id);
            // Definir o input
            setInput(initialMessage);
          }
        } catch (error) {
          logger.error("Erro ao criar sessão:", error);
          pendingInitialMessageRef.current = null;
        } finally {
          sessionCreationInProgressRef.current = false;
        }
      };
      
      createSession();
    }
  }, [initialMessage]);

  // Efeito para enviar automaticamente quando o input for preenchido com a mensagem inicial
  useEffect(() => {
    if (pendingInitialMessageRef.current && input === pendingInitialMessageRef.current && currentSession && messages.length === 0) {
      logger.debug("Input preenchido com mensagem inicial, enviando automaticamente...");
      const messageToSend = pendingInitialMessageRef.current;
      pendingInitialMessageRef.current = null;
      
      // Enviar após um pequeno delay usando streaming para ter formatação linha a linha
      setTimeout(() => {
        logger.debug("Disparando handleStreamChat com a mensagem:", messageToSend);
        handleStreamChat();
      }, 300);
    }
  }, [input, currentSession, messages.length]);

  // Efeito para enviar mensagem da tela inicial quando a sessão for criada
  useEffect(() => {
    if (pendingInitialScreenMessageRef.current && currentSession && messages.length === 0 && input.trim()) {
      logger.debug("Sessão criada, enviando mensagem pendente da tela inicial:", pendingInitialScreenMessageRef.current);
      pendingInitialScreenMessageRef.current = null;
      
      // Enviar a mensagem
      setTimeout(() => {
        handleStreamChat();
      }, 100);
    }
  }, [currentSession, messages.length, input]);

  // Efeito para processar áudio inicial do dashboard
  useEffect(() => {
    if (initialAudioFile && !initialAudioSentRef.current && !sessionCreationInProgressRef.current) {
      logger.debug("Áudio inicial detectado do dashboard");
      initialAudioSentRef.current = true;
      sessionCreationInProgressRef.current = true;
      
      // Criar nova sessão e processar áudio
      const createAndProcess = async () => {
        try {
          const session = await handleCreateSession();
          if (session) {
            logger.debug("Sessão criada, processando áudio inicial");
            await handleRecordingComplete(initialAudioFile);
            // Marcar que a transcrição está pronta
            audioTranscriptionReadyRef.current = true;
          }
        } catch (error) {
          logger.error("Erro ao processar áudio inicial:", error);
        } finally {
          sessionCreationInProgressRef.current = false;
        }
      };
      
      createAndProcess();
    }
  }, [initialAudioFile]);

  // Efeito para enviar automaticamente após transcrição do áudio estar pronta
  useEffect(() => {
    const shouldAutoSend = audioTranscriptionReadyRef.current && 
                          currentSession && 
                          fileTranscriptions.size > 0 && 
                          attachedFiles.length > 0;
    
    if (shouldAutoSend) {
      logger.debug("Transcrição de áudio pronta, enviando automaticamente...");
      logger.debug("Arquivos anexados:", attachedFiles.length);
      logger.debug("Transcrições disponíveis:", fileTranscriptions.size);
      logger.debug("Sessão atual:", currentSession?.id);
      logger.debug("Mensagens atuais:", messages.length);
      
      audioTranscriptionReadyRef.current = false;
      
      // Enviar após um delay para garantir que tudo foi atualizado
      // Usar handleStreamChat diretamente pois ele lida com arquivos e transcrições
      setTimeout(() => {
        logger.debug("Disparando handleStreamChat após transcrição de áudio");
        // Forçar handleStreamChat a processar mesmo sendo primeira mensagem
        handleStreamChat();
      }, 1000);
    }
  }, [fileTranscriptions, currentSession, attachedFiles, messages.length]);

  // Efeito para controlar sidebar em mobile
  // Sidebar sempre inicia fechada - usuário abre se quiser ver histórico

  // Auto-hide sidebar quando começar a digitar em mobile
  useEffect(() => {
    if (isMobile && isTyping && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [isMobile, isTyping, sidebarOpen]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
    if (e.target.value.length > 0) {
      setIsTyping(true);
    } else {
      setIsTyping(false);
    }
  };

  const detectDocumentIntent = (userMessage: string, aiResponse: string = ''): boolean => {
    const generationVerbs = ['redigir', 'escrever', 'criar', 'elaborar', 'gerar', 'fazer', 'preparar', 'montar', 'produzir'];
    const documentNouns = [
      'petição', 'peticao', 'inicial', 'contestação', 'contestacao',
      'recurso', 'apelação', 'apelacao', 'agravo', 'embargos',
      'contrato', 'modelo', 'mandado', 'sentença', 'sentenca',
      'acórdão', 'acordao', 'defesa', 'manifestação', 'manifestacao',
      'notificação', 'notificacao', 'intimação', 'intimacao',
      'parecer', 'procuração', 'procuracao', 'documento', 'peça', 'peca'
    ];

    const combined = (userMessage + ' ' + aiResponse).toLowerCase();
    const hasVerb = generationVerbs.some(v => combined.includes(v));
    const hasNoun = documentNouns.some(n => combined.includes(n));
    return hasVerb && hasNoun;
  };

  const detectDocumentIntentEarly = (userMessage: string): boolean => {
    const msg = userMessage.toLowerCase();
    const generationVerbs = ['redigir', 'escrever', 'criar', 'elaborar', 'gerar', 'fazer', 'preparar', 'montar'];
    const documentNouns = [
      'petição', 'peticao', 'inicial', 'contestação', 'contestacao',
      'recurso', 'apelação', 'apelacao', 'agravo', 'embargos',
      'contrato', 'mandado', 'defesa', 'notificação', 'notificacao',
      'parecer', 'procuração', 'procuracao', 'documento jurídico', 'documento juridico',
      'peça', 'peca'
    ];
    const hasVerb = generationVerbs.some(v => msg.includes(v));
    const hasNoun = documentNouns.some(n => msg.includes(n));
    return hasVerb && hasNoun;
  };

  // Função para navegar ao fluxo de geração de documentos com contexto do chat
  const handleNavigateToWizard = () => {
    const conversationContext = messages
      .slice(-6)
      .map(msg => `${msg.isUser ? 'Usuário' : 'Assistente'}: ${msg.content}`)
      .join('\n\n');
    
    navigate('/documents/flow/documento-livre', {
      state: {
        context: conversationContext,
        fromChat: true,
        sessionId: currentSession?.id
      }
    });
  };

  const handleInputFocus = () => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  const loadSession = async (id: string) => {
    try {
      setIsLoadingHistory(true); // Usar estado específico para histórico
      const session = await getChatSession(id);
      setCurrentSession(session);
      const convertedMessages = session.messages?.map(msg => ({
        id: msg.id,
        content: msg.content,
        isUser: msg.role === 'user',
        created_at: msg.created_at,
        session_id: session.id,
        tokens_used: msg.tokens_used,
        provider: msg.provider,
        role: msg.role,
        attachments: (msg as any).attachments || []
      })) || [];
      setMessages(convertedMessages);
      setInput('');
    } catch (error) {
      logger.error('Error loading session:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // ⚡ REMOVIDO: loadSessions - agora usa React Query
  // React Query carrega automaticamente com cache de 2 minutos

  const handleCreateSession = async (preserveInput: boolean = false) => {
    try {
      setIsLoading(true);
      
      // Descarta só a conversa aberta, se o usuário ainda não escreveu nada nela.
      // A listagem do backend não traz mensagens (sempre []), então não serve para decidir isso:
      // usá-la apagava a conversa mais recente do usuário.
      const emptySession = currentSession && !isLoadingHistory && !messages.some(msg => msg.isUser || msg.role === 'user')
        ? currentSession
        : undefined;
      
      // Se encontrou uma conversa vazia, delete ela primeiro
      if (emptySession) {
        try {
          await deleteChatSession(emptySession.id);
          logger.debug("Conversa vazia deletada:", emptySession.id);
        } catch (error: any) {
          // Ignorar erro 404 (sessão já foi deletada)
          if (error?.response?.status === 404) {
            logger.debug('Sessão vazia já não existe (404), continuando...');
          } else {
            logger.error('Error deleting empty session:', error);
          }
          // Continua mesmo se falhar ao deletar a sessão vazia
        }
      }
      
      const newSession = await createChatSession();
      
      // Garante que temos um ID de sessão válido
      if (!newSession || !newSession.id) {
        throw new Error("Failed to create a valid session");
      }
      
      // ⚡ React Query refetch automático
      queryClient.invalidateQueries({ queryKey: ['chatSessions'] });
      setMessages([]);
      setCurrentSession(newSession);
      setShowInitialScreen(false); // Ocultar tela inicial ao criar sessão

      // Reflete o session_id na URL para que o reload preserve a conversa
      // (route: /chat/:sessionId). Se já estamos numa rota com sessionId,
      // não empurramos duplicado.
      if (newSession.id && routeSessionId !== newSession.id) {
        navigate(`/chat/${newSession.id}`, { replace: true });
      }

      // Só limpa o input se não for para preservar (quando vem da tela inicial com mensagem)
      if (!preserveInput) {
        setInput('');
      }

      setIsLoadingHistory(false); // Limpar estado de carregamento de histórico

      logger.debug("Nova sessão criada:", newSession);

      return newSession;
    } catch (error) {
      logger.error('Error creating session:', error);
      setError('Erro ao criar nova conversa. Tente novamente.');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreSessions = async () => {
    if (loadingMoreSessions || !hasMoreSessions) return;
    
    setLoadingMoreSessions(true);
    try {
      // ⚡ Carregar mais sessões via API direta (paginação manual)
      const skip = sessionsSkip;
      const newSessions = await getChatSessions(skip + 25, 25);
      setLocalSessions(prevSessions => [...prevSessions, ...newSessions]);
      setSessionsSkip(skip + 25);
      setHasMoreSessions(newSessions.length === 25);
    } catch (error) {
      logger.error('Error loading more sessions:', error);
    } finally {
      setLoadingMoreSessions(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, session: ChatSession) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedSessionForMenu(session);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSessionForMenu(null);
  };

  const handleDeleteFromMenu = () => {
    if (selectedSessionForMenu) {
      handleDeleteClick(selectedSessionForMenu);
    }
    handleMenuClose();
  };

  // Nova função para lidar com novas conversas, usando o método não-streaming para a primeira mensagem
  const handleFirstMessage = async () => {
    logger.debug("handleFirstMessage chamado");
    
    if (!input.trim() || !currentSession) {
      logger.debug("Saindo - input vazio ou session inválida");
      return;
    }
    
    logger.debug("Enviando primeira mensagem para sessão:", currentSession.id);

    const userMessage: Message = {
      id: `temp-user-${Date.now()}`,
      content: input,
      isUser: true,
      created_at: new Date(),
      session_id: currentSession.id,
      role: 'user'
    };

    // Guardar o input atual e depois limpar
    const messageToSend = input;
    setInput('');
    logger.debug("Mensagem a enviar:", messageToSend);
    
    // Adicione a mensagem do usuário ao estado
    setMessages(prev => {
      logger.debug("Adicionando mensagem do usuário ao estado");
      return [...prev, userMessage];
    });
    setIsLoading(true);

    // REMOVIDO: detectDocumentIntentEarly - sugestão agora aparece apenas após resposta da IA

    try {
      // Garante que temos uma sessão válida
      if (!currentSession.id) {
        throw new Error("Invalid session ID");
      }

      logger.debug("Enviando primeira mensagem não-streaming para sessão ID:", currentSession.id);
      
      // Usa o método sendMessage ao invés de streamChat para a primeira mensagem
      const response = await sendMessage({
        message: messageToSend,
        session_id: currentSession.id
      });

      logger.debug("Resposta da API recebida:", response);

      const assistantMessage: Message = {
        id: '',
        content: response.message,
        isUser: false,
        created_at: new Date(),
        session_id: currentSession.id,
        tokens_used: response.tokens_used,
        provider: response.provider,
        role: 'assistant'
      };

      setMessages(prev => {
        logger.debug("Adicionando resposta do assistente ao estado");
        return [...prev, assistantMessage];
      });
      
      // Detectar se deve sugerir o wizard de documentos
      if (response.message && messageToSend) {
        const shouldSuggestWizard = detectDocumentIntent(messageToSend, response.message);
        if (shouldSuggestWizard) {
          logger.debug("Detectada intenção de geração de documento");
          setShowWizardSuggestion(true);
          setWizardContext(messageToSend);
        }
      }
      
      // Atualizar créditos após enviar mensagem
      // refreshCredits(); // This line is removed as per the new_code
      
      logger.debug("Primeira mensagem enviada com sucesso");
    } catch (error) {
      logger.error('Error sending first message:', error);
      setError('Erro ao enviar a mensagem. Tente novamente.');
      
      // Se houver um erro, adicione uma mensagem de erro
      setMessages(prev => {
        logger.debug("Adicionando mensagem de erro");
        return [...prev, {
          id: `error-${Date.now()}`,
          content: "Desculpe, ocorreu um erro ao processar sua solicitação. Por favor, tente novamente.",
          isUser: false,
          created_at: new Date(),
          session_id: currentSession.id,
          role: 'assistant'
        }];
      });
    } finally {
      logger.debug("Finalizando handleFirstMessage");
      setIsLoading(false);
    }
  };

  const handleSelectSession = async (session: ChatSession) => {
    try {
      setIsLoadingHistory(true);
      setShowInitialScreen(false); // Ocultar tela inicial ao selecionar sessão
      // Refletir a sessão selecionada na URL para que o reload preserve o contexto
      if (session.id && routeSessionId !== session.id) {
        navigate(`/chat/${session.id}`, { replace: true });
      }
      const fullSession = await getChatSession(session.id);
      setCurrentSession(fullSession);
      const convertedMessages = fullSession.messages?.map(msg => ({
        id: msg.id,
        content: msg.content,
        isUser: msg.role === 'user',
        created_at: msg.created_at,
        session_id: fullSession.id,
        tokens_used: msg.tokens_used,
        provider: msg.provider,
        role: msg.role,
        attachments: (msg as any).attachments || []
      })) || [];
      setMessages(convertedMessages);
      setInput('');
      setError(null);
      
      // Aguardar as mensagens serem setadas antes de parar o loading
      if (convertedMessages.length > 0) {
        setTimeout(() => {
          setIsLoadingHistory(false);
        }, 100);
      } else {
        setIsLoadingHistory(false);
      }
    } catch (error) {
      logger.error('Error loading session:', error);
      setError('Erro ao carregar conversa. Tente novamente.');
      setIsLoadingHistory(false);
    }
  };

  const handleDeleteClick = (session: ChatSession) => {
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;
    
    try {
      await deleteChatSession(sessionToDelete.id);
      setLocalSessions(sessions.filter(s => s.id !== sessionToDelete.id));
      if (currentSession?.id === sessionToDelete.id) {
        setCurrentSession(null);
        setMessages([]);
      }
      setDeleteDialogOpen(false);
    } catch (error) {
      logger.error('Error deleting session:', error);
      setError('Erro ao excluir a conversa. Tente novamente.');
    }
  };

  // Carregar sessões favoritas
  const loadFavoriteSessions = async () => {
    try {
      const favorites = await getFavoriteSessions();
      setFavoriteSessions(favorites);
    } catch (error) {
      logger.error('Error loading favorite sessions:', error);
    }
  };

  // Toggle favorito de uma sessão
  const handleToggleFavorite = async (session: ChatSession, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    try {
      const newFavoriteStatus = !session.is_favorite;
      await toggleFavoriteSession(session.id, newFavoriteStatus);
      
      // Atualizar sessões locais
      setLocalSessions(prev => prev.map(s => 
        s.id === session.id ? { ...s, is_favorite: newFavoriteStatus } : s
      ));
      
      // Atualizar sessão atual se for a mesma
      if (currentSession?.id === session.id) {
        setCurrentSession(prev => prev ? { ...prev, is_favorite: newFavoriteStatus } : null);
      }
      
      // Recarregar lista de favoritos
      loadFavoriteSessions();
      
      // Invalidar cache
      queryClient.invalidateQueries({ queryKey: ['chatSessions'] });
    } catch (error) {
      logger.error('Error toggling favorite:', error);
      setError('Erro ao atualizar favorito.');
    }
  };

  // Abrir dialog de renomear
  const handleOpenRenameDialog = (session: ChatSession, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSessionToRename(session);
    setNewSessionName(session.custom_title || session.title || '');
    setRenameDialogOpen(true);
  };

  // Renomear sessão
  const handleRenameSession = async () => {
    if (!sessionToRename || !newSessionName.trim()) return;
    
    try {
      await renameChatSession(sessionToRename.id, newSessionName.trim());
      
      // Atualizar sessões locais
      setLocalSessions(prev => prev.map(s => 
        s.id === sessionToRename.id ? { 
          ...s, 
          custom_title: newSessionName.trim(),
          display_title: newSessionName.trim()
        } : s
      ));
      
      // Atualizar sessão atual se for a mesma
      if (currentSession?.id === sessionToRename.id) {
        setCurrentSession(prev => prev ? { 
          ...prev, 
          custom_title: newSessionName.trim(),
          display_title: newSessionName.trim()
        } : null);
      }
      
      // Recarregar favoritos
      loadFavoriteSessions();
      
      // Invalidar cache
      queryClient.invalidateQueries({ queryKey: ['chatSessions'] });
      
      setRenameDialogOpen(false);
      setSessionToRename(null);
      setNewSessionName('');
    } catch (error) {
      logger.error('Error renaming session:', error);
      setError('Erro ao renomear conversa.');
    }
  };

  // Carregar favoritos na montagem
  useEffect(() => {
    loadFavoriteSessions();
  }, []);

  // Funções para gerenciar upload de arquivos
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const filesArray = Array.from(files);
      setAttachedFiles(prev => [...prev, ...filesArray]);
      
      // Reset input para permitir selecionar o mesmo arquivo novamente
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Upload automático para transcrição imediata
      await uploadFilesForTranscription(filesArray);
    }
  };

  const handleRemoveFile = (index: number) => {
    const fileToRemove = attachedFiles[index];
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    
    // Remover transcrição e ID também
    setFileTranscriptions(prev => {
      const newMap = new Map(prev);
      newMap.delete(fileToRemove.name);
      // Resetar expansão se não houver mais transcrições
      if (newMap.size === 0) {
        setTranscriptionExpanded(false);
      }
      return newMap;
    });
    
    setFileIdMap(prev => {
      const newMap = new Map(prev);
      newMap.delete(fileToRemove.name);
      return newMap;
    });
  };

  // Função para lidar com gravação de áudio completa
  const handleRecordingComplete = async (audioFile: File) => {
    logger.debug(`Áudio gravado: ${audioFile.name} (${audioFile.size} bytes)`);
    setAttachedFiles(prev => [...prev, audioFile]);
    
    // Upload automático para transcrição imediata
    await uploadFilesForTranscription([audioFile]);
  };
  
  // Upload automático para obter transcrição antes de enviar
  const uploadFilesForTranscription = async (files: File[]) => {
    setUploadingFiles(true);
    const newTranscriptionMap = new Map(fileTranscriptions);
    const newFileIdMap = new Map(fileIdMap);
    
    try {
      for (const file of files) {
        try {
          const result = await uploadChatAttachment(file);
          logger.info(`File uploaded for transcription: ${result.filename} (ID: ${result.file_id})`);
          
          // Armazenar ID do arquivo
          newFileIdMap.set(file.name, result.file_id);
          
          // Se houver transcrição (áudio/documento), armazenar
          if (result.transcription) {
            newTranscriptionMap.set(file.name, result.transcription);
            logger.info(`Transcription received for ${file.name}: ${result.transcription.substring(0, 100)}...`);
          }
        } catch (error) {
          logger.error(`Error uploading file ${file.name}:`, error);
          setError(`Erro ao processar arquivo ${file.name}`);
        }
      }
      
      // Atualizar estados
      setFileTranscriptions(newTranscriptionMap);
      setFileIdMap(newFileIdMap);
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleUploadFiles = async (): Promise<{ fileIds: string[], transcriptions: string[], fileTypes: string[] }> => {
    if (attachedFiles.length === 0) return { fileIds: [], transcriptions: [], fileTypes: [] };
    
    // Usar IDs já armazenados (arquivos já foram enviados para transcrição)
    const fileIds: string[] = [];
    const transcriptions: string[] = [];
    const fileTypes: string[] = [];
    
    for (const file of attachedFiles) {
      // Buscar ID já armazenado
      const fileId = fileIdMap.get(file.name);
      if (fileId) {
        fileIds.push(fileId);
        logger.info(`Using existing file ID: ${file.name} -> ${fileId}`);
      } else {
        logger.warn(`File ID not found for ${file.name}, skipping`);
      }
      
      // Buscar transcrição já armazenada
      const transcription = fileTranscriptions.get(file.name);
      if (transcription) {
        transcriptions.push(transcription);
      }
      
      // Identificar tipo de arquivo
      const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
      const audioExtensions = ['mp3', 'wav', 'm4a', 'ogg', 'flac', 'webm'];
      const isAudio = audioExtensions.includes(fileExt);
      fileTypes.push(isAudio ? 'audio' : 'document');
    }
    
    return { fileIds, transcriptions, fileTypes };
  };

  const handleStreamChat = async () => {
    logger.debug("handleStreamChat chamado");
    
    if ((!input.trim() && attachedFiles.length === 0) || !currentSession) {
      logger.debug("Saindo - input vazio e sem arquivos ou session inválida");
      return;
    }
    
    logger.debug("Enviando mensagem para sessão:", currentSession.id);

    // Upload dos arquivos primeiro
    let uploadedIds: string[] = [];
    let transcriptions: string[] = [];
    let fileTypes: string[] = [];
    if (attachedFiles.length > 0) {
      logger.debug(`Fazendo upload de ${attachedFiles.length} arquivo(s)`);
      const uploadResult = await handleUploadFiles();
      uploadedIds = uploadResult.fileIds;
      transcriptions = uploadResult.transcriptions;
      fileTypes = uploadResult.fileTypes;
      logger.debug(`Upload concluído. IDs: ${uploadedIds.join(', ')}`);
      logger.debug(`Transcrições recebidas: ${transcriptions.length}`);
    }

    // Se não há texto mas há transcrição, usar a transcrição como texto
    let finalMessage = input.trim();
    let displayMessage = input.trim();
    
    if (!finalMessage && transcriptions.length > 0) {
      // Usar a transcrição como mensagem
      finalMessage = transcriptions.join('\n\n');
      
      // Verificar se é áudio ou documento
      const isAudio = fileTypes[0] === 'audio';
      const fileTypeLabel = isAudio ? 'Áudio' : 'Documento';
      displayMessage = `${fileTypeLabel} processado: "${transcriptions[0].substring(0, 100)}${transcriptions[0].length > 100 ? '...' : ''}"`;
      logger.info('Usando transcrição como mensagem:', finalMessage);
    } else if (!finalMessage && attachedFiles.length > 0) {
      // Tem arquivo mas não tem transcrição (imagem, etc)
      finalMessage = 'Analise o(s) arquivo(s) anexado(s)';
      displayMessage = 'Arquivo(s) anexado(s)';
    }

    // Construir attachments para exibicao otimista imediata na mensagem
    const optimisticAttachments = attachedFiles.map((file, idx) => {
      const fileId = uploadedIds[idx];
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      const isImg = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
      const isAud = ['mp3', 'wav', 'm4a', 'webm', 'ogg'].includes(ext);
      const type: 'image' | 'audio' | 'document' = isImg ? 'image' : (isAud ? 'audio' : 'document');
      return {
        id: fileId || `temp-${Date.now()}-${idx}`,
        name: file.name,
        size: file.size,
        type,
        url: fileId ? `/api/v1/chat/attachments/${fileId}/download` : '',
        preview: fileId && isImg ? `/api/v1/chat/attachments/${fileId}/preview` : null
      };
    });

    const userMessage: Message = {
      id: `temp-user-${Date.now()}`, // ID temporário para garantir a unicidade
      content: displayMessage,
      isUser: true,
      created_at: new Date(),
      session_id: currentSession.id,
      role: 'user',
      attachments: optimisticAttachments
    };

    // Guardar o input atual e depois limpar
    const messageToSend = finalMessage;
    setInput(''); // Limpar o campo de entrada imediatamente
    setAttachedFiles([]); // Limpar arquivos anexados
    setFileTranscriptions(new Map()); // Limpar transcrições
    setTranscriptionExpanded(false); // Resetar expansão da transcrição
    setFileIdMap(new Map()); // Limpar IDs dos arquivos
    logger.debug("Mensagem a enviar:", messageToSend);

    // Adicione a mensagem do usuário ao estado
    setMessages(prev => {
      logger.debug("Adicionando mensagem do usuário ao estado");
      return [...prev, userMessage];
    });
    setIsLoading(true);

    // REMOVIDO: detectDocumentIntentEarly - sugestão agora aparece apenas após resposta da IA

    try {
      // Garante que temos uma sessão válida
      if (!currentSession.id) {
        throw new Error("Invalid session ID");
      }

      // Crie uma mensagem do assistente vazia para começar
      const assistantMessage: Message = {
        id: `temp-assistant-${Date.now()}`, // ID temporário para garantir a unicidade
        content: '',
        isUser: false,
        created_at: new Date(),
        session_id: currentSession.id,
        role: 'assistant',
        isStreaming: true // Flag para indicar que está em streaming
      };

      // Adicione a mensagem vazia ao estado
      setMessages(prev => {
        logger.debug("Adicionando mensagem vazia do assistente");
        return [...prev, assistantMessage];
      });

      logger.debug("Iniciando streaming para sessão ID:", currentSession.id);

      // Variável para acumular a resposta
      let accumulatedResponse = '';

      // Faz a chamada de streaming e atualiza a mensagem do assistente à medida que os chunks chegam
      await streamChat(
        {
          message: messageToSend,
          session_id: currentSession.id,
          attachments: uploadedIds // Incluir IDs dos arquivos anexados
        },
        (chunk) => {
          logger.debug("Chunk recebido:", chunk.substring(0, 50) + "...");
          // Acumula a resposta
          accumulatedResponse += chunk;
          
          // Atualiza a última mensagem (a do assistente) com o novo chunk completo
          setMessages(prev => {
            const newMessages = [...prev];
            // A última mensagem deve ser do assistente
            const lastMessageIndex = newMessages.length - 1;
            if (lastMessageIndex >= 0 && !newMessages[lastMessageIndex].isUser) {
              // Cria uma nova mensagem em vez de mutar a existente
              newMessages[lastMessageIndex] = {
                ...newMessages[lastMessageIndex],
                content: accumulatedResponse,
                id: `streaming-${Date.now()}-${Math.random()}`, // ID único para forçar re-render
                isStreaming: true
              };
            }
            return newMessages; // Retorna o array com a mensagem atualizada
          });
          
          // Força o scroll para o final a cada chunk
          setTimeout(scrollToBottom, 10);
        }
      );

      logger.debug("Streaming concluído com sucesso");
      
      // Finaliza o streaming e remove a flag
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessageIndex = newMessages.length - 1;
        if (lastMessageIndex >= 0 && !newMessages[lastMessageIndex].isUser) {
          newMessages[lastMessageIndex] = {
            ...newMessages[lastMessageIndex],
            isStreaming: false,
            id: `final-${Date.now()}` // ID final para a mensagem
          };
        }
        return newMessages;
      });
      
      // Atualizar créditos após enviar mensagem
      try {
        await refreshUserData();
      } catch (e) {
        // Silenciar erro de atualização
      }
      
      // Detectar se deve sugerir o wizard de documentos
      if (accumulatedResponse && userMessage.content) {
        const shouldSuggestWizard = detectDocumentIntent(userMessage.content, accumulatedResponse);
        if (shouldSuggestWizard) {
          logger.debug("Detectada intenção de geração de documento");
          setShowWizardSuggestion(true);
          setWizardContext(userMessage.content);
        }
      }
      
      // Força o scroll após terminar
      setTimeout(scrollToBottom, 100);
      setTimeout(scrollToBottom, 300);
    } catch (error) {
      logger.error('Error streaming chat:', error);
      const semCreditos = error instanceof Error && /créditos insuficientes/i.test(error.message);
      if (semCreditos) {
        setShowLowCreditPopup(true);
      }

      // Se houver um erro, adicione uma mensagem de erro
      setMessages(prev => {
        const newMessages = [...prev];
        // Se a última mensagem for do assistente (vazia), substitua-a com uma mensagem de erro
        if (newMessages.length > 0 && !newMessages[newMessages.length - 1].isUser) {
          newMessages[newMessages.length - 1] = {
            ...newMessages[newMessages.length - 1],
            isStreaming: false,
            content: semCreditos
              ? "Seus créditos acabaram. Adicione créditos para continuar usando o chat."
              : "Desculpe, ocorreu um erro ao processar sua solicitação. Por favor, tente novamente."
          };
        }
        return newMessages;
      });
    } finally {
      logger.debug("Finalizando handleStreamChat");
      setIsLoading(false);
      // Força o scroll após terminar o loading
      setTimeout(scrollToBottom, 200);
    }
  };

  const loadMessages = async () => {
    if (!activeSessionId) return;
    
    setIsLoadingHistory(true); // Usar estado específico para histórico
    try {
      const session = await getChatSession(activeSessionId);
      setCurrentSession(session);
      const convertedMessages = session.messages?.map(msg => ({
        id: msg.id,
        content: msg.content,
        isUser: msg.role === 'user',
        created_at: msg.created_at,
        session_id: session.id,
        tokens_used: msg.tokens_used,
        provider: msg.provider,
        role: msg.role,
        attachments: (msg as any).attachments || []
      })) || [];
      setMessages(convertedMessages);
      setInput('');
    } catch (error) {
      logger.error('Error loading messages:', error);
      setError('Erro ao carregar as mensagens. Tente novamente.');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Custom renderer for markdown elements
  const MarkdownComponents = {
    // Títulos
    h1: ({ children }: any) => (
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        {children}
      </Typography>
    ),
    h2: ({ children }: any) => (
      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mt: 2 }}>
        {children}
      </Typography>
    ),
    h3: ({ children }: any) => (
      <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mt: 1.5 }}>
        {children}
      </Typography>
    ),
    h4: ({ children }: any) => (
      <Typography variant="subtitle1" component="h4" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
        {children}
      </Typography>
    ),
    // Parágrafos
    p: ({ children }: any) => (
      <Typography variant="body1" paragraph sx={{ mb: 1.5, lineHeight: 1.6 }}>
        {children}
      </Typography>
    ),
    // Listas
    ul: ({ children }: any) => (
      <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>
        {children}
      </Box>
    ),
    ol: ({ children }: any) => (
      <Box component="ol" sx={{ pl: 2, mb: 1.5 }}>
        {children}
      </Box>
    ),
    li: ({ children }: any) => (
      <Typography component="li" variant="body1" sx={{ mb: 0.5, lineHeight: 1.6 }}>
        {children}
      </Typography>
    ),
    // Código
    code({ node, inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={atomDark}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <Box component="code" sx={{ 
          bgcolor: 'grey.100', 
          px: 0.5, 
          py: 0.25, 
          borderRadius: 0.5,
          fontFamily: 'monospace',
          fontSize: '0.875em'
        }}>
          {children}
        </Box>
      );
    },
    // Texto em negrito
    strong: ({ children }: any) => (
      <Box component="strong" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
        {children}
      </Box>
    ),
    // Quebras de linha
    br: () => <br />
  };

  // Parser de markdown customizado para streaming em tempo real
  const parseMarkdownRealtime = (text: string) => {
    // Primeiro, quebra o texto em linhas e processa headers que podem estar inline
    let processedText = text;
    
    // Substitui ### no meio do texto por quebras de linha
    processedText = processedText.replace(/([^#])### /g, '$1\n### ');
    processedText = processedText.replace(/^### /gm, '\n### ');
    
    // Substitui - no início de frases por quebras de linha para listas
    processedText = processedText.replace(/([.!?]) - /g, '$1\n- ');
    
    // Substitui padrões como "**texto** - **texto**:" por quebras de linha
    processedText = processedText.replace(/(\*\*[^*]+\*\*)\s*-\s*(\*\*[^*]+\*\*:)/g, '$1\n- $2');
    
    // Quebra quando há dois negritos seguidos (título + subtítulo)
    processedText = processedText.replace(/(\*\*[^*]+\*\*)\s*-\s*(\*\*[^*]+\*\*)/g, '$1\n- $2');
    
    // NOVO: Quebra listas numeradas que estão inline
    // Detecta padrões como "texto. 2." ou "texto. 2. **" e adiciona quebra de linha
    processedText = processedText.replace(/([.!?])\s+(\d+)\.\s+/g, '$1\n$2. ');
    
    // NOVO: Também quebra quando começa com número no meio do texto (após qualquer pontuação)
    processedText = processedText.replace(/([a-zá-ú])\.\s+(\d+)\.\s+/gi, '$1.\n$2. ');
    
    const lines = processedText.split('\n');
    const elements: JSX.Element[] = [];
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      const key = `line-${index}-${trimmedLine.length}-${Math.random()}`;
      
      // Headers
      if (trimmedLine.startsWith('### ')) {
        elements.push(
          <Typography key={key} variant="subtitle1" component="h3" gutterBottom 
            sx={{ fontWeight: 'bold', color: 'primary.main', mt: 1.5, mb: 0.5, fontSize: '1.1rem' }}>
            {parseInlineMarkdown(trimmedLine.replace('### ', ''))}
          </Typography>
        );
      } else if (trimmedLine.startsWith('## ')) {
        elements.push(
          <Typography key={key} variant="subtitle1" component="h2" gutterBottom 
            sx={{ fontWeight: 'bold', color: 'primary.main', mt: 1.5, mb: 0.5, fontSize: '1.2rem' }}>
            {parseInlineMarkdown(trimmedLine.replace('## ', ''))}
          </Typography>
        );
      } else if (trimmedLine.startsWith('# ')) {
        elements.push(
          <Typography key={key} variant="subtitle1" component="h1" gutterBottom 
            sx={{ fontWeight: 'bold', color: 'primary.main', mt: 1.5, mb: 0.5, fontSize: '1.3rem' }}>
            {parseInlineMarkdown(trimmedLine.replace('# ', ''))}
          </Typography>
        );
      }
      // Títulos numerados (1. **Título**)
      else if (/^\d+\.\s*\*\*[^*]+\*\*\s*$/.test(trimmedLine)) {
        elements.push(
          <Typography key={key} variant="subtitle1" component="h3" gutterBottom 
            sx={{ fontWeight: 'bold', color: 'primary.main', mt: 1.5, mb: 0.5, fontSize: '1.1rem' }}>
            {parseInlineMarkdown(trimmedLine)}
          </Typography>
        );
      }
      // Lista com -
      else if (trimmedLine.startsWith('- ')) {
        elements.push(
          <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, ml: 1 }}>
            <Typography sx={{ mr: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.9rem' }}>•</Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.5, flex: 1, fontSize: '0.95rem' }}>
              {parseInlineMarkdown(trimmedLine.replace('- ', ''))}
            </Typography>
          </Box>
        );
      }
      // Lista com *
      else if (trimmedLine.startsWith('* ')) {
        elements.push(
          <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, ml: 1 }}>
            <Typography sx={{ mr: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.9rem' }}>•</Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.5, flex: 1, fontSize: '0.95rem' }}>
              {parseInlineMarkdown(trimmedLine.replace('* ', ''))}
            </Typography>
          </Box>
        );
      }
      // Lista numerada (1., 2., 3., etc.)
      else if (/^\d+\.\s/.test(trimmedLine)) {
        const match = trimmedLine.match(/^(\d+)\.\s(.+)$/);
        if (match) {
          const number = match[1];
          const content = match[2];
          elements.push(
            <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.8, ml: 1 }}>
              <Typography sx={{ 
                mr: 1.5, 
                color: 'primary.main', 
                fontWeight: 'bold', 
                fontSize: '0.9rem',
                minWidth: '20px'
              }}>
                {number}.
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.6, flex: 1, fontSize: '0.95rem' }}>
                {parseInlineMarkdown(content)}
              </Typography>
            </Box>
          );
        }
      }
      // Linha vazia
      else if (trimmedLine === '') {
        elements.push(<Box key={key} sx={{ height: '8px' }} />);
      }
      // Parágrafo normal
      else if (trimmedLine !== '') {
        elements.push(
          <Typography key={key} variant="body2" paragraph 
            sx={{ mb: 1, lineHeight: 1.5, fontSize: '0.95rem' }}>
            {parseInlineMarkdown(trimmedLine)}
          </Typography>
        );
      }
    });
    
    return elements;
  };

  // Parser para markdown inline (negrito, itálico, etc.)
  const parseInlineMarkdown = (text: string) => {
    const parts: (string | JSX.Element)[] = [];
    let currentText = text;
    let partIndex = 0;

    // Processa **negrito**
    while (currentText.includes('**')) {
      const firstBold = currentText.indexOf('**');
      const secondBold = currentText.indexOf('**', firstBold + 2);
      
      if (secondBold !== -1) {
        // Adiciona texto antes do negrito
        if (firstBold > 0) {
          parts.push(currentText.substring(0, firstBold));
        }
        
        // Adiciona texto em negrito (sem os **)
        const boldText = currentText.substring(firstBold + 2, secondBold);
        if (boldText.trim()) { // Só adiciona se não for vazio
          parts.push(
            <Box key={`bold-${partIndex++}`} component="strong" 
              sx={{ fontWeight: 'bold', color: 'text.primary', display: 'inline' }}>
              {boldText}
            </Box>
          );
        }
        
        // Continue com o resto do texto (pulando os **)
        currentText = currentText.substring(secondBold + 2);
      } else {
        // Se não encontrar o segundo **, adiciona o resto incluindo o primeiro **
        parts.push(currentText);
        break;
      }
    }
    
    // Adiciona o resto do texto (se houver)
    if (currentText && currentText.trim()) {
      parts.push(currentText);
    }
    
    // Se processou alguma coisa, retorna as parts, senão retorna o texto original
    return parts.length > 0 ? parts : [text];
  };

  // Função para renderizar mensagem com parser customizado
  const renderMarkdownMessage = (content: string, messageId: string, isStreaming?: boolean) => {
    const elements = parseMarkdownRealtime(content);
    
    return (
      <Box key={`custom-${messageId}-${content.length}-${isStreaming ? Date.now() : 'final'}`}>
        {elements}
      </Box>
    );
  };

  // ⚡ Skeleton Screen - mostra estrutura instantaneamente
  if (sessionsLoading && sessions.length === 0) {
    return <ChatSkeleton />;
  }
  
  return (
    <ChatContainer>
      {error && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          bgcolor: 'error.main', 
          color: 'error.contrastText',
          borderRadius: 1
        }}>
          <Typography>{error}</Typography>
        </Box>
      )}
      
      {/* Botão toggle da sidebar */}
      <SidebarToggle 
        size="small"
        onClick={toggleSidebar}
        sx={{ display: isMobile ? 'flex' : 'none' }}
      >
        <MenuIcon />
      </SidebarToggle>



      <ChatLayout>
        {/* Mini Sidebar com ícones - sempre visível no desktop */}
        {!isMobile && (
          <Box
            sx={{
              width: 60,
              minWidth: 60,
              height: '100%',
              backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f8f9fa',
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 2,
              gap: 1,
              position: 'relative',
              zIndex: 1000
            }}
          >
            {/* Botão Nova Conversa */}
            <Tooltip title="Nova conversa" placement="right">
              <IconButton
                onClick={() => handleCreateSession()}
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  }
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>

            {/* Botão Histórico */}
            <Tooltip title={sidebarOpen ? "Fechar histórico" : "Ver histórico"} placement="right">
              <IconButton
                onClick={toggleSidebar}
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: sidebarOpen ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>

            <Box sx={{ flexGrow: 1 }} />

            {/* Conversas recentes (ícones) */}
            {sessions.slice(0, 5).map((session, index) => (
              <Tooltip key={session.id} title={session.title || `Conversa ${index + 1}`} placement="right">
                <IconButton
                  onClick={() => handleSelectSession(session)}
                  sx={{
                    width: 40,
                    height: 40,
                    fontSize: '0.75rem',
                    backgroundColor: currentSession?.id === session.id 
                      ? alpha(theme.palette.primary.main, 0.15) 
                      : 'transparent',
                    border: currentSession?.id === session.id 
                      ? `2px solid ${theme.palette.primary.main}` 
                      : '2px solid transparent',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    }
                  }}
                >
                  <ChatIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        )}

        {/* Sidebar expandida com histórico completo */}
        {!isMobile && sidebarOpen && (
          <Sidebar sx={{ 
            transform: 'translateX(0)',
            left: 60, // Posicionar após a mini sidebar
            width: 280,
          }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              mb: 2
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Histórico</Typography>
                <IconButton size="small" onClick={() => setSidebarOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <TextField
                size="small"
                placeholder="Buscar conversas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
            </Box>
            {/* Seção de Favoritos */}
            {favoriteSessions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5, 
                    px: 1, 
                    py: 0.5, 
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1
                  }}
                  onClick={() => setShowFavorites(!showFavorites)}
                >
                  <StarIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>
                    Favoritos ({favoriteSessions.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {showFavorites ? '▼' : '▶'}
                  </Typography>
                </Box>
                {showFavorites && (
                  <List dense sx={{ py: 0 }}>
                    {favoriteSessions.map((session) => (
                      <ListItem
                        key={`fav-${session.id}`}
                        button
                        selected={currentSession?.id === session.id}
                        onClick={() => {
                          handleSelectSession(session);
                          setSidebarOpen(false);
                        }}
                        sx={{ pl: 3, py: 0.5 }}
                      >
                        <ListItemText
                          primary={session.display_title || session.custom_title || session.title || 'Nova conversa'}
                          primaryTypographyProps={{ noWrap: true, sx: { maxWidth: 140, fontSize: '0.85rem' } }}
                        />
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => handleToggleFavorite(session, e)}
                          sx={{ p: 0.5 }}
                        >
                          <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
            
            {/* Lista de todas as conversas */}
            <Typography variant="caption" sx={{ px: 1, color: 'text.secondary', fontWeight: 600 }}>
              Todas as conversas
            </Typography>
            <List sx={{ '& .MuiListItem-root': { pr: 12 } }}>
              {filteredSessions.map((session) => (
                <ListItem
                  key={session.id}
                  button
                  selected={currentSession?.id === session.id}
                  onClick={() => {
                    handleSelectSession(session);
                    setSidebarOpen(false);
                  }}
                  sx={{ py: 1 }}
                >
                  {session.is_favorite && (
                    <StarIcon sx={{ color: 'warning.main', fontSize: 16, mr: 1, flexShrink: 0 }} />
                  )}
                  <ListItemText
                    primary={session.display_title || session.custom_title || session.title || 'Nova conversa'}
                    secondary={new Date(session.created_at).toLocaleDateString()}
                    primaryTypographyProps={{ 
                      noWrap: true, 
                      sx: { 
                        fontSize: '0.9rem',
                        fontWeight: session.is_favorite ? 500 : 400
                      } 
                    }}
                    secondaryTypographyProps={{ sx: { fontSize: '0.75rem' } }}
                  />
                  <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Tooltip title={session.is_favorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleToggleFavorite(session, e)}
                        sx={{ p: 0.5 }}
                      >
                        {session.is_favorite ? (
                          <StarIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                        ) : (
                          <StarBorderIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Renomear">
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenRenameDialog(session, e)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      aria-label="menu"
                      size="small"
                      onClick={(e) => handleMenuClick(e, session)}
                      sx={{ p: 0.5 }}
                    >
                      <MoreVertIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            {hasMoreSessions && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="text" 
                  color="inherit" 
                  onClick={loadMoreSessions}
                  disabled={loadingMoreSessions}
                  size="small"
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  {loadingMoreSessions ? (
                    <>
                      <CircularProgress size={12} sx={{ mr: 1 }} />
                      Carregando...
                    </>
                  ) : (
                    'Carregar mais conversas...'
                  )}
                </Button>
              </Box>
            )}
          </Sidebar>
        )}

        {/* Drawer para mobile */}
        {isMobile && (
          <Drawer
            anchor="left"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            sx={{
              zIndex: 998,
              '& .MuiDrawer-paper': {
                width: '320px',
                padding: '16px',
              },
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              mb: 2
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Histórico de Conversas</Typography>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<AddIcon />}
                onClick={() => handleCreateSession()}
                fullWidth
                sx={{ mb: 2 }}
              >
                Nova Conversa
              </Button>
              <TextField
                size="small"
                placeholder="Buscar conversas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
            </Box>
            {/* Seção de Favoritos (Mobile) */}
            {favoriteSessions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5, 
                    px: 1, 
                    py: 0.5, 
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1
                  }}
                  onClick={() => setShowFavorites(!showFavorites)}
                >
                  <StarIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>
                    Favoritos ({favoriteSessions.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {showFavorites ? '▼' : '▶'}
                  </Typography>
                </Box>
                {showFavorites && (
                  <List dense sx={{ py: 0 }}>
                    {favoriteSessions.map((session) => (
                      <ListItem
                        key={`fav-mobile-${session.id}`}
                        button
                        selected={currentSession?.id === session.id}
                        onClick={() => {
                          handleSelectSession(session);
                          setSidebarOpen(false);
                        }}
                        sx={{ pl: 3, py: 0.5 }}
                      >
                        <ListItemText
                          primary={session.display_title || session.custom_title || session.title || 'Nova conversa'}
                          primaryTypographyProps={{ noWrap: true, fontSize: '0.85rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
            
            <Typography variant="caption" sx={{ px: 1, color: 'text.secondary', fontWeight: 600 }}>
              Todas as conversas
            </Typography>
            <List sx={{ '& .MuiListItem-root': { pr: 10 } }}>
              {filteredSessions.map((session) => (
                <ListItem
                  key={session.id}
                  button
                  selected={currentSession?.id === session.id}
                  onClick={() => {
                    handleSelectSession(session);
                    setSidebarOpen(false);
                  }}
                  sx={{ py: 1 }}
                >
                  {session.is_favorite && (
                    <StarIcon sx={{ color: 'warning.main', fontSize: 16, mr: 1, flexShrink: 0 }} />
                  )}
                  <ListItemText
                    primary={session.display_title || session.custom_title || session.title || 'Nova conversa'}
                    secondary={new Date(session.created_at).toLocaleDateString()}
                    primaryTypographyProps={{ 
                      noWrap: true, 
                      sx: { fontWeight: session.is_favorite ? 500 : 400 } 
                    }}
                    secondaryTypographyProps={{ sx: { fontSize: '0.75rem' } }}
                  />
                  <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleToggleFavorite(session, e)}
                      sx={{ p: 0.5 }}
                    >
                      {session.is_favorite ? (
                        <StarIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                      ) : (
                        <StarBorderIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      )}
                    </IconButton>
                    <IconButton
                      aria-label="menu"
                      size="small"
                      onClick={(e) => handleMenuClick(e, session)}
                      sx={{ p: 0.5 }}
                    >
                      <MoreVertIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            {hasMoreSessions && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="text" 
                  color="inherit" 
                  onClick={loadMoreSessions}
                  disabled={loadingMoreSessions}
                  size="small"
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  {loadingMoreSessions ? (
                    <>
                      <CircularProgress size={12} sx={{ mr: 1 }} />
                      Carregando...
                    </>
                  ) : (
                    'Carregar mais conversas...'
                  )}
                </Button>
              </Box>
            )}
          </Drawer>
        )}

        <MainContent sidebarOpen={sidebarOpen}>
          {currentSession ? (
            <>
              {/* TELA INICIAL - Sem mensagens */}
              {!isLoadingHistory && (!messages || messages.length === 0) ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                    p: 3,
                    animation: 'fadeIn 0.5s ease-out',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'translateY(20px)' },
                      to: { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}
                >
                  {/* Logo e saudação */}
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box
                      component="img"
                      src="/favicon.svg"
                      alt="Logo"
                      sx={{
                        width: 80,
                        height: 80,
                        mb: 2,
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.05)' }
                        }
                      }}
                    />
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700, 
                        color: 'text.primary',
                        mb: 1
                      }}
                    >
                      Como posso ajudar?
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Pergunte sobre direito, peça ajuda com documentos ou tire suas dúvidas jurídicas.
                    </Typography>
                  </Box>

                  {/* Input centralizado */}
                  <Paper
                    elevation={0}
                    sx={{
                      width: '100%',
                      maxWidth: 700,
                      p: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.03),
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      borderRadius: 4,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      },
                      '&:focus-within': {
                        backgroundColor: 'background.paper',
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                      }
                    }}
                  >
                    {/* Chips dos arquivos anexados */}
                    {attachedFiles.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          maxHeight: { xs: '30vh', md: '25vh' },
                          overflowY: 'auto',
                          p: 0.5
                        }}>
                          {attachedFiles.map((file, index) => (
                            <Chip
                              key={index}
                              label={`${file.name} (${(file.size / 1024).toFixed(1)}KB)`}
                              onDelete={() => handleRemoveFile(index)}
                              deleteIcon={<CloseIcon />}
                              color="primary"
                              variant="outlined"
                              size="small"
                              sx={{ maxWidth: '100%' }}
                            />
                          ))}
                        </Box>
                        {fileTranscriptions.size > 0 && (
                          <Alert severity="success" sx={{ mt: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              Áudio/Documento transcrito com sucesso
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      {/* Input de arquivo oculto */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.webp,.mp3,.wav,.m4a"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                      />
                      
                      <Tooltip title="Anexar arquivo">
                        <span style={{ position: 'relative', zIndex: 1100 }}>
                          <IconButton
                            color="primary"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isLoading || isBlocked || uploadingFiles}
                            sx={{ mb: 0.5, position: 'relative', zIndex: 1100 }}
                          >
                            <AttachFileIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      
                      <Box sx={{ position: 'relative', zIndex: 1100 }}>
                        <AudioRecorder
                          onRecordingComplete={handleRecordingComplete}
                          disabled={isLoading || isBlocked || uploadingFiles}
                        />
                      </Box>
                      
                      <TextField
                        fullWidth
                        multiline
                        maxRows={6}
                        variant="standard"
                        placeholder={isBlocked ? "Recarregue seus créditos..." : "Digite sua solicitação ou dúvida jurídica..."}
                        value={input}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey && !isBlocked) {
                            e.preventDefault();
                            handleStreamChat();
                          }
                        }}
                        disabled={isLoading || isBlocked || uploadingFiles}
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '1.1rem' }
                        }}
                        sx={{ 
                          '& .MuiInputBase-root': { 
                            py: 1,
                          }
                        }}
                      />
                      
                      <IconButton
                        color="primary"
                        onClick={handleStreamChat}
                        disabled={(!input.trim() && attachedFiles.length === 0) || isLoading || isBlocked || uploadingFiles}
                        sx={{ 
                          mb: 0.5,
                          backgroundColor: (input.trim() || attachedFiles.length > 0) ? 'primary.main' : 'transparent',
                          color: (input.trim() || attachedFiles.length > 0) ? 'white' : 'primary.main',
                          '&:hover': {
                            backgroundColor: (input.trim() || attachedFiles.length > 0) ? 'primary.dark' : alpha(theme.palette.primary.main, 0.1),
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {isLoading || uploadingFiles ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                      </IconButton>
                    </Box>
                  </Paper>

                  {/* Sugestões rápidas */}
                  <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', maxWidth: 700 }}>
                    {[
                      'Como fazer uma petição inicial?',
                      'Prazo para recurso de apelação',
                      'Diferença entre dano moral e material',
                      'Requisitos da ação de alimentos'
                    ].map((suggestion) => (
                      <Chip
                        key={suggestion}
                        label={suggestion}
                        onClick={() => {
                          setInput(suggestion);
                        }}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            transform: 'translateY(-2px)'
                          }
                        }}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              ) : (
                /* TELA DE CHAT - Com mensagens */
                <>
              <MessagesContainer ref={messagesContainerRef}>
                {isLoadingHistory && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      Carregando conversa...
                    </Typography>
                  </Box>
                )}
                {!isLoadingHistory && messages && messages.length > 0 && messages.map((message, index) => (
                  <MessageBubble isUser={message.isUser} key={`${message.id || `msg-${index}`}-${message.content?.length || 0}`}>
                    {message.isUser ? (
                      <>
                        <Typography variant="body1" sx={{
                          color: 'white',
                          whiteSpace: 'pre-wrap', // Preserva quebras de linha e espaços
                          wordBreak: 'break-word'
                        }}>
                          {message.content}
                        </Typography>
                        <MessageAttachments attachments={(message as any).attachments} isUser={true} />
                      </>
                    ) : (
                      <>
                        {renderMarkdownMessage(
                          message.content,
                          message.id || `msg-${index}`,
                          message.isStreaming
                        )}
                        <MessageAttachments attachments={(message as any).attachments} isUser={false} />
                        {/* Feedback para mensagens da IA */}
                        {!message.isStreaming && message.content && (
                          <ChatMessageFeedback
                            messageId={message.id || `msg-${index}`}
                            sessionId={currentSession?.id || ''}
                            messageContent={message.content}
                          />
                        )}
                      </>
                    )}
                  </MessageBubble>
                ))}
                
                {/* Loading indicator quando está processando */}
                {isLoading && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      p: 4,
                      backgroundColor: 'background.paper',
                      borderRadius: 3,
                      maxWidth: '300px',
                      margin: '0 auto',
                      mb: 2,
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      animation: 'fadeInUp 0.4s ease-out',
                      '@keyframes fadeInUp': {
                        from: { 
                          opacity: 0, 
                          transform: 'translateY(20px) scale(0.95)' 
                        },
                        to: { 
                          opacity: 1, 
                          transform: 'translateY(0) scale(1)' 
                        }
                      }
                    }}
                  >
                    {/* Logo com animações combinadas */}
                    <Box
                      sx={{
                        position: 'relative',
                        width: 64,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {/* Círculo de fundo pulsante */}
                      <Box
                        sx={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          animation: 'pulseRing 2s ease-in-out infinite',
                          '@keyframes pulseRing': {
                            '0%': { 
                              transform: 'scale(0.95)',
                              opacity: 1
                            },
                            '50%': { 
                              transform: 'scale(1.1)',
                              opacity: 0.7
                            },
                            '100%': { 
                              transform: 'scale(0.95)',
                              opacity: 1
                            }
                          }
                        }}
                      />
                      
                      {/* Logo com rotação suave e escala */}
                      <Box
                        component="img"
                        src="/favicon.svg"
                        alt="Advogada Parceira"
                        sx={{
                          width: 40,
                          height: 40,
                          zIndex: 1,
                          animation: 'logoFloat 3s ease-in-out infinite',
                          '@keyframes logoFloat': {
                            '0%, 100%': { 
                              transform: 'translateY(0) scale(1)'
                            },
                            '50%': { 
                              transform: 'translateY(-8px) scale(1.05)'
                            }
                          }
                        }}
                      />
                    </Box>
                    
                    {/* Texto e bolinhas */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 600, 
                          color: theme.palette.primary.main,
                          textAlign: 'center'
                        }}
                      >
                        Advogada Parceira
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          textAlign: 'center'
                        }}
                      >
                        Analisando sua solicitação...
                      </Typography>
                      
                      {/* Bolinhas animadas */}
                      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, #EB5D36 0%, #FF8F4E 100%)`,
                            animation: 'bounce 1.4s ease-in-out 0s infinite',
                            '@keyframes bounce': {
                              '0%, 80%, 100%': { 
                                transform: 'translateY(0) scale(1)',
                                opacity: 0.6
                              },
                              '40%': { 
                                transform: 'translateY(-12px) scale(1.2)',
                                opacity: 1
                              }
                            }
                          }}
                        />
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, #EB5D36 0%, #FF8F4E 100%)`,
                            animation: 'bounce 1.4s ease-in-out 0.2s infinite',
                            '@keyframes bounce': {
                              '0%, 80%, 100%': { 
                                transform: 'translateY(0) scale(1)',
                                opacity: 0.6
                              },
                              '40%': { 
                                transform: 'translateY(-12px) scale(1.2)',
                                opacity: 1
                              }
                            }
                          }}
                        />
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, #EB5D36 0%, #FF8F4E 100%)`,
                            animation: 'bounce 1.4s ease-in-out 0.4s infinite',
                            '@keyframes bounce': {
                              '0%, 80%, 100%': { 
                                transform: 'translateY(0) scale(1)',
                                opacity: 0.6
                              },
                              '40%': { 
                                transform: 'translateY(-12px) scale(1.2)',
                                opacity: 1
                              }
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
                
                {/* Sugestão de fluxos de documentos com sugestões dinâmicas */}
                {showWizardSuggestion && wizardContext && (
                  <FlowSuggestionCard
                    key={wizardContext}
                    context={wizardContext}
                    conversationContext={messages
                      .slice(-6)
                      .map(msg => `${msg.isUser ? 'Usuário' : 'Assistente'}: ${msg.content}`)
                      .join('\n\n')}
                    sessionId={currentSession?.id}
                    onDismiss={() => setShowWizardSuggestion(false)}
                  />
                )}
                
                <div 
                  ref={messagesEndRef} 
                  style={{ height: '50px', width: '100%', clear: 'both' }} 
                  id="chat-end-anchor"
                />
              </MessagesContainer>

              <InputContainer>
                {/* Input de arquivo oculto */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.webp,.mp3,.wav,.m4a"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                
                {/* Chips dos arquivos anexados */}
                {attachedFiles.length > 0 && (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 1,
                    backgroundColor: 'background.default',
                    borderRadius: 1,
                    width: '100%',
                    maxHeight: { xs: '30vh', md: '25vh' },
                    overflowY: 'auto'
                  }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {attachedFiles.map((file, index) => (
                        <Chip
                          key={index}
                          label={`${file.name} (${(file.size / 1024).toFixed(1)}KB)`}
                          onDelete={() => handleRemoveFile(index)}
                          deleteIcon={<CloseIcon />}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ maxWidth: '100%' }}
                        />
                      ))}
                    </Box>
                    
                    {/* Mostrar transcrições se disponíveis */}
                    {fileTranscriptions.size > 0 && (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            Áudio/Documento transcrito com sucesso
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => setTranscriptionExpanded(!transcriptionExpanded)}
                            sx={{ ml: 1, p: 0.5 }}
                          >
                            {transcriptionExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Box>
                        {Array.from(fileTranscriptions.entries()).map(([filename, transcription], idx) => (
                          <Box key={idx} sx={{ mt: 0.5 }}>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 'medium' }}>
                              <strong>{filename}:</strong>
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block', 
                                mt: 0.25,
                                whiteSpace: transcriptionExpanded ? 'pre-wrap' : 'nowrap',
                                overflow: transcriptionExpanded ? 'visible' : 'hidden',
                                textOverflow: transcriptionExpanded ? 'clip' : 'ellipsis',
                                maxHeight: transcriptionExpanded ? 'none' : '1.5em',
                                backgroundColor: transcriptionExpanded ? 'rgba(0,0,0,0.03)' : 'transparent',
                                padding: transcriptionExpanded ? 1 : 0,
                                borderRadius: 1,
                                wordBreak: 'break-word'
                              }}
                            >
                              "{transcriptionExpanded ? transcription : `${transcription.substring(0, 150)}...`}"
                            </Typography>
                            {!transcriptionExpanded && transcription.length > 150 && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block', 
                                  color: 'primary.main', 
                                  cursor: 'pointer',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={() => setTranscriptionExpanded(true)}
                              >
                                Ver transcrição completa ({transcription.length} caracteres)
                              </Typography>
                            )}
                          </Box>
                        ))}
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                          {transcriptionExpanded 
                            ? 'O texto completo acima será enviado para a IA processar'
                            : 'Você pode enviar diretamente sem escrever nada, ou adicionar mais contexto'}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                )}
                
                {/* Linha única: [📎] [🎤] [textarea] [➤] */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, width: '100%' }}>
                  {/* Botão de anexar */}
                  <Tooltip title="Anexar arquivo (PDF, DOC, TXT, Imagem, Áudio)">
                    <span style={{ position: 'relative', zIndex: 1100 }}>
                      <IconButton
                        color="primary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isLoading || isBlocked || uploadingFiles}
                        sx={{ position: 'relative', zIndex: 1100 }}
                      >
                        <AttachFileIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Gravador de áudio */}
                  <Box sx={{ position: 'relative', zIndex: 1100, display: 'flex', alignItems: 'center' }}>
                    <AudioRecorder
                      onRecordingComplete={handleRecordingComplete}
                      disabled={isLoading || isBlocked || uploadingFiles}
                    />
                  </Box>

                  {/* TextField existente */}
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    variant="outlined"
                    placeholder={isBlocked ? "Recarregue seus créditos para continuar..." : "Digite sua mensagem... (Enter para enviar, Shift+Enter para quebrar linha)"}
                    value={input}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !isBlocked) {
                        e.preventDefault();
                        handleStreamChat();
                      }
                    }}
                    disabled={isLoading || isBlocked || uploadingFiles}
                  />

                  {/* Botão de enviar */}
                  <IconButton
                    color="primary"
                    onClick={handleStreamChat}
                    disabled={(!input.trim() && attachedFiles.length === 0) || isLoading || isBlocked || uploadingFiles}
                    sx={{
                      backgroundColor: (input.trim() || attachedFiles.length > 0) ? 'primary.main' : 'transparent',
                      color: (input.trim() || attachedFiles.length > 0) ? 'white' : 'primary.main',
                      '&:hover': {
                        backgroundColor: (input.trim() || attachedFiles.length > 0) ? 'primary.dark' : alpha(theme.palette.primary.main, 0.1),
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'transparent',
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isLoading || uploadingFiles ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                  </IconButton>
                </Box>
              </InputContainer>
                </>
              )}
            </>
          ) : (
            /* TELA INICIAL - Sem sessão ativa */
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
                p: 3,
                animation: 'fadeInInitial 0.6s ease-out',
                '@keyframes fadeInInitial': {
                  from: { opacity: 0, transform: 'translateY(30px)' },
                  to: { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              {/* Logo animado com círculo pulsante */}
              <Box
                sx={{
                  position: 'relative',
                  width: 90,
                  height: 90,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    animation: 'pulseRing 2s infinite',
                    '@keyframes pulseRing': {
                      '0%': { transform: 'scale(0.8)', opacity: 0.5 },
                      '50%': { transform: 'scale(1)', opacity: 1 },
                      '100%': { transform: 'scale(0.8)', opacity: 0.5 },
                    },
                  }}
                />
                <Box
                  component="img"
                  src="/favicon.svg"
                  alt="Logo Advogada Parceira"
                  sx={{
                    width: 55,
                    height: 55,
                    animation: 'logoFloat 3s ease-in-out infinite',
                    '@keyframes logoFloat': {
                      '0%': { transform: 'translateY(0px)' },
                      '50%': { transform: 'translateY(-6px)' },
                      '100%': { transform: 'translateY(0px)' },
                    },
                  }}
                />
              </Box>

              {/* Título */}
              <Typography 
                variant="h5" 
                component="h1" 
                sx={{ 
                  mb: 1, 
                  fontWeight: 700, 
                  color: 'text.primary',
                  textAlign: 'center'
                }}
              >
                Como posso ajudar você hoje?
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2.5, 
                  color: 'text.secondary',
                  textAlign: 'center',
                  maxWidth: 450
                }}
              >
                Sua assistente jurídica especializada no sistema legal brasileiro.
              </Typography>

              {/* Sugestões rápidas */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 2.5, maxWidth: 550 }}>
                {[
                  'Criar petição inicial',
                  'Analisar contrato',
                  'Dúvida trabalhista',
                  'Pesquisar jurisprudência',
                  'Redigir defesa'
                ].map((suggestion, index) => (
                  <Chip
                    key={index}
                    label={suggestion}
                    onClick={async () => {
                      setShowInitialScreen(false);
                      const session = await handleCreateSession();
                      if (session) {
                        setInput(suggestion);
                      }
                    }}
                    clickable
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{
                      fontSize: '0.8rem',
                      px: 1,
                      py: 1.5,
                      borderRadius: '16px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.15)}`,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Input centralizado */}
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 700,
                  p: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  borderRadius: 4,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderColor: alpha(theme.palette.primary.main, 0.25),
                  },
                  '&:focus-within': {
                    backgroundColor: 'background.paper',
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                  }
                }}
              >
                {/* Chips dos arquivos anexados */}
                {attachedFiles.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      maxHeight: { xs: '30vh', md: '25vh' },
                      overflowY: 'auto',
                      p: 0.5
                    }}>
                      {attachedFiles.map((file, index) => (
                        <Chip
                          key={index}
                          label={`${file.name} (${(file.size / 1024).toFixed(1)}KB)`}
                          onDelete={() => handleRemoveFile(index)}
                          deleteIcon={<CloseIcon />}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ maxWidth: '100%' }}
                        />
                      ))}
                    </Box>
                    {fileTranscriptions.size > 0 && (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          Áudio/Documento transcrito com sucesso
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                  {/* Input de arquivo oculto */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.webp,.mp3,.wav,.m4a"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />

                  <Tooltip title="Anexar arquivo">
                    <span style={{ position: 'relative', zIndex: 1100 }}>
                      <IconButton
                        color="primary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isLoading || isBlocked || uploadingFiles}
                        sx={{ mb: 0.5, position: 'relative', zIndex: 1100 }}
                      >
                        <AttachFileIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  
                  <Box sx={{ position: 'relative', zIndex: 1100 }}>
                    <AudioRecorder
                      onRecordingComplete={handleRecordingComplete}
                      disabled={isLoading || isBlocked || uploadingFiles}
                    />
                  </Box>
                  
                  <TextField
                    fullWidth
                    multiline
                    maxRows={6}
                    variant="standard"
                    placeholder={isBlocked ? "Recarregue seus créditos..." : "Digite sua solicitação ou dúvida jurídica..."}
                    value={input}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !isBlocked && input.trim()) {
                        e.preventDefault();
                        pendingInitialScreenMessageRef.current = input.trim();
                        setShowInitialScreen(false);
                        await handleCreateSession(true); // Preservar input - o useEffect vai enviar
                      }
                    }}
                    disabled={isLoading || isBlocked || uploadingFiles}
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: '1rem' }
                    }}
                    sx={{ flex: 1, mb: 0.5 }}
                  />
                  
                  <IconButton
                    color="primary"
                    onClick={async () => {
                      if (input.trim() || attachedFiles.length > 0) {
                        pendingInitialScreenMessageRef.current = input.trim() || 'arquivo';
                        setShowInitialScreen(false);
                        await handleCreateSession(true); // Preservar input - o useEffect vai enviar
                      }
                    }}
                    disabled={(!input.trim() && attachedFiles.length === 0) || isLoading || isBlocked || uploadingFiles}
                    sx={{ mb: 0.5 }}
                  >
                    {isLoading || uploadingFiles ? <CircularProgress size={24} /> : <SendIcon />}
                  </IconButton>
                </Box>
              </Paper>
            </Box>
          )}
        </MainContent>
      </ChatLayout>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          if (selectedSessionForMenu) {
            handleOpenRenameDialog(selectedSessionForMenu);
          }
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} />
          Renomear
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSessionForMenu) {
            handleToggleFavorite(selectedSessionForMenu);
          }
          handleMenuClose();
        }}>
          {selectedSessionForMenu?.is_favorite ? (
            <>
              <StarIcon sx={{ mr: 1, color: 'warning.main' }} />
              Remover dos favoritos
            </>
          ) : (
            <>
              <StarBorderIcon sx={{ mr: 1 }} />
              Adicionar aos favoritos
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleDeleteFromMenu} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Excluir conversa
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir esta conversa? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteSession} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Renomear */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => {
          setRenameDialogOpen(false);
          setSessionToRename(null);
          setNewSessionName('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Renomear conversa</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome da conversa"
            fullWidth
            value={newSessionName}
            onChange={(e) => setNewSessionName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newSessionName.trim()) {
                handleRenameSession();
              }
            }}
            placeholder="Digite o novo nome..."
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setRenameDialogOpen(false);
              setSessionToRename(null);
              setNewSessionName('');
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleRenameSession} 
            color="primary" 
            variant="contained"
            disabled={!newSessionName.trim()}
          >
            Renomear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback geral da sessão de chat */}
      {currentSession && messages.length > 0 && (
        <FeedbackWidget
          feature="chat"
          entityType="chat_session"
          entityId={currentSession.id}
          title="Avalie esta conversa"
          position="bottom-left"
          compact
        />
      )}

    </ChatContainer>
  );
};

export default Chat;
